import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  PlusIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  CurrencyRupeeIcon,
  EyeIcon,
  EyeSlashIcon,
  BuildingLibraryIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { supabase } from '../../config/supabase'
import cashfreeService from '../../services/enhancedCashfreeService'
import toast from 'react-hot-toast'

const BankAccountManagement = ({ restaurant, user, onClose }) => {
  const [bankAccounts, setBankAccounts] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showAccountNumber, setShowAccountNumber] = useState({})
  const [statusCheckLoading, setStatusCheckLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    bankName: '',
    accountType: 'current',
    // KYC Details
    panCard: '',
    gstNumber: '',
    businessType: 'restaurant',
    accountTypeKyc: 'BUSINESS'
  })

  // Load existing bank accounts on component mount
  useEffect(() => {
    loadExistingBankAccounts()
  }, [restaurant?.id])

  const loadExistingBankAccounts = async () => {
    if (!restaurant?.id) return

    try {
      setIsLoading(true)
      
      // First check with backend API for real-time vendor status
      await checkVendorStatusFromAPI()
      
      // Load from localStorage
      const savedAccount = localStorage.getItem(`bank_account_${restaurant.id}`)
      if (savedAccount) {
        const accountData = JSON.parse(savedAccount)
        
        const account = {
          id: 'primary',
          accountNumber: accountData.bank_account_number,
          ifscCode: accountData.bank_ifsc_code,
          accountHolderName: accountData.bank_account_holder_name,
          vendorId: accountData.vendor_id,
          cashfreeStatus: accountData.cashfree_status,
          settlementSchedule: accountData.settlement_schedule,
          splitPaymentsEnabled: accountData.split_payments_enabled,
          isPrimary: true,
          status: accountData.bank_verification_status,
          businessType: accountData.kyc_details?.business_type,
          email: accountData.kyc_details?.email,
          phone: accountData.kyc_details?.phone
        }

        setBankAccounts([account])
        
        // Check current status from Cashfree if vendor_id exists
        if (accountData.vendor_id) {
          await checkVendorStatus(accountData.vendor_id, account)
        }
      }
    } catch (error) {
      console.error('Error loading bank accounts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const checkForDuplicateBankAccount = async () => {
    try {
      // Check localStorage for existing bank account
      const savedAccount = localStorage.getItem(`bank_account_${restaurant.id}`)
      if (savedAccount) {
        const accountData = JSON.parse(savedAccount)
        if (accountData.bank_account_number === formData.accountNumber) {
          console.log('🚫 Duplicate bank account found in localStorage')
          return true
        }
      }

      // Check existing bankAccounts state
      const existingAccount = bankAccounts.find(account => 
        account.accountNumber === formData.accountNumber
      )
      if (existingAccount) {
        console.log('🚫 Duplicate bank account found in current state')
        return true
      }

      // Check with backend/Cashfree for existing vendor with same bank account
      try {
        // Cashfree requires vendor_id to be alphanumeric only (no hyphens or underscores)
        const cleanRestaurantId = restaurant.id.replace(/[-_]/g, '')
        const vendorId = `vendor${cleanRestaurantId}`
        const vendorCheck = await cashfreeService.getVendorDetails(vendorId)
        if (vendorCheck.success && vendorCheck.data?.bank?.account_number === formData.accountNumber) {
          console.log('🚫 Duplicate bank account found in Cashfree')
          return true
        }
      } catch (error) {
        // If vendor doesn't exist, that's expected for new vendors - not an error
        if (error.message.includes('vendor does not exist') || 
            error.message.includes('404') ||
            (error.response && error.response.status === 400 && error.response.data?.message?.includes('vendor does not exist'))) {
          console.log('ℹ️ Vendor does not exist yet - this is expected for new vendors')
        } else {
          console.log('ℹ️ Vendor check failed:', error.message)
        }
      }

      return false
    } catch (error) {
      console.error('Error checking for duplicate bank account:', error)
      return false
    }
  }

  const checkVendorStatusFromAPI = async () => {
    try {
      console.log('🔍 Checking vendor status from backend API...')
      
      const response = await fetch('http://localhost:5000/api/cashfree/check-vendor-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          restaurant_id: restaurant.id,
          restaurant_name: restaurant.name || restaurant.restaurant_name,
          owner_email: user?.email
        })
      })

      const result = await response.json()
      console.log('📊 Vendor status API result:', result)

      if (result.success && result.data?.vendor_registered) {
        const vendorData = result.data
        
        // Create account object from API data
        const account = {
          id: 'primary',
          accountNumber: vendorData.bank_details?.account_number,
          ifscCode: vendorData.bank_details?.ifsc,
          accountHolderName: vendorData.bank_details?.account_holder,
          vendorId: vendorData.vendor_id,
          cashfreeStatus: vendorData.vendor_status,
          settlementSchedule: vendorData.settlement_schedule,
          splitPaymentsEnabled: true,
          isPrimary: true,
          status: vendorData.vendor_status === 'ACTIVE' ? 'verified' : 
                 vendorData.vendor_status === 'IN_BANK_VALIDATION' ? 'validating' : 
                 vendorData.vendor_status === 'BANK_VALIDATION_FAILED' ? 'failed' : 'pending',
          kycDetails: vendorData.kyc_status
        }

        setBankAccounts([account])
        
        // Update localStorage with API data
        const bankAccountData = {
          bank_account_number: account.accountNumber,
          bank_ifsc_code: account.ifscCode,
          bank_account_holder_name: account.accountHolderName,
          vendor_id: account.vendorId,
          cashfree_status: account.cashfreeStatus,
          settlement_schedule: account.settlementSchedule,
          split_payments_enabled: true,
          bank_verification_status: account.status,
          kyc_details: account.kycDetails,
          updated_at: new Date().toISOString()
        }
        
        localStorage.setItem(`bank_account_${restaurant.id}`, JSON.stringify(bankAccountData))
        
        // Show status message
        if (vendorData.vendor_status === 'ACTIVE') {
          toast.success('✅ Bank account is active and ready for settlements!')
        } else if (vendorData.vendor_status === 'KYC_PENDING') {
          toast.info('📋 KYC validation in progress. Please wait for approval.')
        }
      }
    } catch (error) {
      console.error('Error checking vendor status from API:', error)
      // Don't show error toast here as this is a background check
    }
  }

  const checkVendorStatus = async (vendorId, account) => {
    try {
      setStatusCheckLoading(true)
      
      // Call Cashfree API to get current vendor status with detailed information
      const statusResult = await cashfreeService.getVendorDetails(vendorId)
      
      if (statusResult.success) {
        const vendorData = statusResult.data
        
        const updatedAccount = {
          ...account,
          cashfreeStatus: vendorData.status,
          vendorData: vendorData, // Store complete vendor data for detailed status display
          status: vendorData.status === 'ACTIVE' ? 'verified' : 
                 vendorData.status === 'IN_BANK_VALIDATION' ? 'validating' : 
                 vendorData.status === 'BANK_VALIDATION_FAILED' ? 'failed' : 'pending',
          settlementSchedule: vendorData.schedule_option?.settlement_schedule_message,
          kycDetails: vendorData.kyc_details
        }
        
        setBankAccounts([updatedAccount])
        
        // Update localStorage with complete vendor data
        const savedAccount = localStorage.getItem(`bank_account_${restaurant.id}`)
        if (savedAccount) {
          const accountData = JSON.parse(savedAccount)
          accountData.cashfree_status = vendorData.status
          accountData.bank_verification_status = updatedAccount.status
          accountData.vendor_data = vendorData
          accountData.kyc_details = vendorData.kyc_details
          accountData.updated_at = new Date().toISOString()
          localStorage.setItem(`bank_account_${restaurant.id}`, JSON.stringify(accountData))
        }
        
        // Show detailed status update toasts
        if (vendorData.status === 'ACTIVE') {
          toast.success('🎉 Bank account validation completed! Ready for settlements.')
        } else if (vendorData.status === 'IN_BANK_VALIDATION') {
          toast.info('⏳ Bank validation in progress...')
        } else if (vendorData.status === 'BANK_VALIDATION_FAILED') {
          toast.error('❌ Bank account validation failed. Please check your account details.')
        } else if (vendorData.status === 'KYC_PENDING') {
          const issues = getDetailedStatusInfo(vendorData)
          if (issues && issues.length > 0) {
            const panIssue = issues.find(issue => issue.type === 'pan')
            if (panIssue) {
              toast.error('❌ PAN validation failed. Please provide a valid PAN number.')
            }
          } else {
            toast.info('📋 KYC documents pending. Please submit valid PAN and GST details.')
          }
        }
        
        console.log('📊 Updated vendor status:', vendorData)
      } else {
        toast.error('Failed to fetch vendor status. Please try again.')
      }
    } catch (error) {
      console.error('Error checking vendor status:', error)
      toast.error('Error checking status: ' + error.message)
    } finally {
      setStatusCheckLoading(false)
    }
  }
  
  const [businessDetails, setBusinessDetails] = useState({
    restaurantName: '',
    email: '',
    phone: '',
    panNumber: '',
    gstNumber: '',
    aadhaar: '',
    cinNumber: ''
  })



  useEffect(() => {
    if (restaurant?.id) {
      fetchBankAccounts()
    }
  }, [restaurant?.id])

  const fetchBankAccounts = async () => {
    if (!restaurant?.id) return
    
    try {
      setIsLoading(true)
      
      // For now, we'll use localStorage to store bank account data temporarily
      // until proper database schema is set up
      const storedBankData = localStorage.getItem(`bank_account_${restaurant.id}`)
      
      if (storedBankData) {
        const bankData = JSON.parse(storedBankData)
        setBankAccounts([{
          id: 'primary',
          accountNumber: bankData.bank_account_number,
          ifscCode: bankData.bank_ifsc_code,
          accountHolderName: bankData.bank_account_holder_name,
          vendorId: bankData.vendor_id,
          splitPaymentsEnabled: bankData.split_payments_enabled,
          isPrimary: true,
          status: bankData.vendor_id ? 'verified' : 'pending'
        }])
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error)
      toast.error('Failed to load bank account details')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleBusinessDetailsChange = (e) => {
    const { name, value } = e.target
    setBusinessDetails(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    // Check if bank account already exists
    if (bankAccounts.length > 0) {
      toast.error('Bank account already exists for this restaurant. You can only have one bank account.')
      return false
    }

    if (!formData.accountNumber || !formData.confirmAccountNumber || !formData.ifscCode || !formData.accountHolderName) {
      toast.error('Please fill in all required bank details')
      return false
    }

    if (formData.accountNumber !== formData.confirmAccountNumber) {
      toast.error('Account numbers do not match')
      return false
    }

    if (formData.ifscCode.length !== 11) {
      toast.error('IFSC code must be 11 characters')
      return false
    }

    // Validate KYC details - either PAN or GST required
    if (!formData.panCard && !formData.gstNumber) {
      toast.error('Either PAN card or GST number is required for KYC verification')
      return false
    }

    // Enhanced PAN validation - more flexible format
    if (formData.panCard) {
      const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/
      const cleanPan = formData.panCard.toUpperCase().replace(/\s/g, '')
      
      if (!panPattern.test(cleanPan)) {
        toast.error('Please enter a valid PAN card number (Format: ABCDE1234F)')
        return false
      }
      
      // Update formData with cleaned PAN
      setFormData(prev => ({ ...prev, panCard: cleanPan }))
    }

    // Validate GST format if provided
    if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber)) {
      toast.error('Please enter a valid GST number (15 characters)')
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    await handleBankVerification()
  }

  const handleBankVerification = async () => {
    if (!validateForm()) return

    try {
      setIsLoading(true)
      
      // Step 0: Check for duplicate bank account
      const isDuplicate = await checkForDuplicateBankAccount()
      if (isDuplicate) {
        toast.error('This bank account is already registered. Please use a different account.')
        return
      }
      
      // Step 1: Verify bank account with Cashfree
      const verificationResult = await cashfreeService.verifyBankAccount({
        accountNumber: formData.accountNumber,
        ifscCode: formData.ifscCode,
        accountHolderName: formData.accountHolderName,
        restaurantId: restaurant?.id
      })

      if (!verificationResult.success) {
        toast.error(verificationResult.error || 'Bank verification failed')
        return
      }

      console.log('✅ Bank account verified, proceeding with vendor creation...')
      console.log('🔍 Debug - formData.accountHolderName:', formData.accountHolderName)
      console.log('🔍 Debug - Full formData:', formData)

      // Step 2: Create vendor account for split payments
      // Cashfree requires vendor_id to be alphanumeric only (no hyphens or underscores)
      const cleanRestaurantId = restaurant.id.replace(/[-_]/g, '')
      const timestamp = Date.now().toString()
      const uniqueVendorId = `vendor${cleanRestaurantId}${timestamp}`
      const vendorResult = await cashfreeService.createVendorAccount({
        vendorId: uniqueVendorId,
        name: restaurant.name || restaurant.restaurant_name,
        email: user.email,
        phone: restaurant.phone || user.phone,
        bankDetails: {
          accountNumber: formData.accountNumber,
          ifscCode: formData.ifscCode,
          name: formData.accountHolderName,
          accountHolderName: formData.accountHolderName
        },
        kycDetails: {
          accountType: formData.accountTypeKyc,
          businessType: formData.businessType,
          pan: formData.panCard || null,
          gst: formData.gstNumber || null
        }
      })

      if (vendorResult.success) {
        // Show detailed success information
        const vendorData = vendorResult.data || vendorResult
        const statusMessage = getStatusMessage(vendorData.status)
        
        toast.success(`🎉 Bank account and vendor setup completed! ${statusMessage}`)
        
        // Step 3: Save to localStorage with complete Cashfree response
        const bankAccountData = {
          bank_account_number: formData.accountNumber,
          bank_ifsc_code: formData.ifscCode,
          bank_account_holder_name: vendorData.bank?.account_holder || formData.accountHolderName,
          vendor_id: vendorData.vendor_id,
          cashfree_status: vendorData.status,
          settlement_schedule: vendorData.schedule_option?.settlement_schedule_message,
          split_payments_enabled: true,
          bank_verification_status: vendorData.status === 'IN_BANK_VALIDATION' ? 'validating' : 'verified',
          kyc_details: vendorData.kyc_details,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
        
        localStorage.setItem(`bank_account_${restaurant?.id}`, JSON.stringify(bankAccountData))
        
        // Add to local state with complete information
        const newAccount = {
          id: 'primary',
          accountNumber: vendorData.bank?.account_number || formData.accountNumber,
          ifscCode: vendorData.bank?.ifsc || formData.ifscCode,
          accountHolderName: vendorData.bank?.account_holder || formData.accountHolderName,
          vendorId: vendorData.vendor_id,
          cashfreeStatus: vendorData.status,
          settlementSchedule: vendorData.schedule_option?.settlement_schedule_message,
          splitPaymentsEnabled: true,
          isPrimary: true,
          status: vendorData.status === 'IN_BANK_VALIDATION' ? 'validating' : 'verified',
          businessType: vendorData.kyc_details?.business_type,
          email: vendorData.email,
          phone: vendorData.phone
        }
        
        setBankAccounts([newAccount])
        
        // Reset form
        setFormData({
          accountNumber: '',
          confirmAccountNumber: '',
          ifscCode: '',
          accountHolderName: '',
          bankName: '',
          accountType: 'current'
        })
        
        setBusinessDetails({
          restaurantName: '',
          email: '',
          phone: '',
          panNumber: '',
          gstNumber: '',
          aadhaar: '',
          cinNumber: ''
        })
        
        setShowAddForm(false)
        
        // Show success message with benefits
        setTimeout(() => {
          toast.success('💰 Your restaurant is now ready to receive direct payments!', {
            duration: 5000,
            icon: '🎉'
          })
        }, 1000)
      } else {
        toast.error(vendorResult.error || 'Failed to create vendor account')
      }
    } catch (error) {
      console.error('Error in bank verification:', error)
      toast.error('Bank verification failed: ' + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return ''
    const str = accountNumber.toString()
    return str.length > 4 ? `****${str.slice(-4)}` : str
  }

  const getStatusMessage = (status) => {
    const statusMessages = {
      'IN_BANK_VALIDATION': 'Bank details are being validated by Cashfree',
      'IN_BENE_CREATION': 'Beneficiary account is being created',
      'ACTIVE': 'Account is active and ready for settlements',
      'BLOCKED': 'Account is temporarily blocked',
      'DELETED': 'Account has been deleted',
      'BANK_VALIDATION_FAILED': 'Bank account validation failed - Check account details and try again',
      'KYC_PENDING': 'KYC documents required - Submit valid PAN and GSTIN',
      'KYC_FAILED': 'KYC validation failed - Please provide valid documents',
      'PAN_VALIDATION_FAILED': 'PAN card validation failed - Please provide a valid PAN number'
    }
    return statusMessages[status] || 'Status unknown'
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      'verified': { color: 'bg-green-100 text-green-800', icon: '✅', text: 'Active' },
      'validating': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳', text: 'Validating' },
      'failed': { color: 'bg-red-100 text-red-800', icon: '❌', text: 'Failed' },
      'pending': { color: 'bg-gray-100 text-gray-800', icon: '⏸️', text: 'Pending' }
    }
    
    const config = statusConfig[status] || statusConfig['pending']
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <span>{config.icon}</span>
        {config.text}
      </span>
    )
  }



  const getDetailedStatusInfo = (vendorData) => {
    if (!vendorData) return null
    
    const issues = []
    
    // Check bank validation status
    if (vendorData.status === 'BANK_VALIDATION_FAILED') {
      issues.push({
        type: 'bank',
        title: 'Bank Account Verification Failed',
        message: 'Unable to validate account details. Please verify account number and IFSC code.',
        action: 'Update Bank Details',
        severity: 'error'
      })
    }
    
    // Check KYC status
    if (vendorData.kyc_details) {
      if (vendorData.kyc_details.pan_status === 'REJECTED') {
        issues.push({
          type: 'pan',
          title: 'PAN Validation Failed',
          message: `PAN ${vendorData.kyc_details.pan} was rejected. Please provide a valid PAN card number.`,
          action: 'Update PAN Details',
          severity: 'error'
        })
      }
      
      if (vendorData.kyc_details.gst_status === 'REJECTED') {
        issues.push({
          type: 'gst',
          title: 'GST Validation Failed',
          message: 'GST number validation failed. Please provide a valid GSTIN.',
          action: 'Update GST Details',
          severity: 'error'
        })
      }
    }
    
    return issues
  }

  const getStatusColor = (status) => {
    const statusColors = {
      'IN_BANK_VALIDATION': 'text-yellow-600 bg-yellow-50',
      'IN_BENE_CREATION': 'text-blue-600 bg-blue-50',
      'ACTIVE': 'text-green-600 bg-green-50',
      'BLOCKED': 'text-red-600 bg-red-50',
      'DELETED': 'text-gray-600 bg-gray-50',
      'BANK_VALIDATION_FAILED': 'text-red-600 bg-red-50',
      'KYC_PENDING': 'text-orange-600 bg-orange-50'
    }
    return statusColors[status] || 'text-gray-600 bg-gray-50'
  }

  const toggleAccountNumberVisibility = (accountId) => {
    setShowAccountNumber(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }))
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
    <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 pb-32 sm:pb-24">
      {/* Compact Mobile Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            {onClose && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </motion.button>
            )}
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                Bank & Payments
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Manage accounts & split payments</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg sm:rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-200 shadow-lg text-sm sm:text-base"
          >
            <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="font-medium sm:font-semibold">Add</span>
            <span className="hidden sm:inline">Account</span>
          </motion.button>
        </div>
      </div>

      {/* Compact Commission Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-4 sm:mb-6 p-3 sm:p-4 md:p-6 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl sm:rounded-2xl border border-orange-200"
      >
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="p-2 sm:p-3 bg-orange-100 rounded-lg sm:rounded-xl">
            <BanknotesIcon className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6 text-orange-600" />
          </div>
          <h3 className="text-sm sm:text-base md:text-lg font-bold text-orange-900">Split Payment Model</h3>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm">
          <div className="p-2 sm:p-3 bg-white rounded-lg border border-orange-100 text-center">
            <p className="text-orange-600 font-medium mb-1">Customer</p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-orange-900">100%</p>
            <p className="text-gray-600 text-xs hidden sm:block">Full amount</p>
          </div>
          <div className="p-2 sm:p-3 bg-white rounded-lg border border-green-100 text-center">
            <p className="text-green-600 font-medium mb-1">You Get</p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-900">96.5%</p>
            <p className="text-gray-600 text-xs hidden sm:block">Direct deposit</p>
          </div>
          <div className="p-2 sm:p-3 bg-white rounded-lg border border-purple-100 text-center">
            <p className="text-purple-600 font-medium mb-1">Platform</p>
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-purple-900">3.5%</p>
            <p className="text-gray-600 text-xs hidden sm:block">Service fee</p>
          </div>
        </div>
      </motion.div>

      {/* Bank Accounts List */}
      {isLoading ? (
        <div className="text-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-3 sm:mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading accounts...</p>
        </div>
      ) : bankAccounts.length === 0 ? (
        <div className="text-center py-8 sm:py-12 md:py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300">
          <div className="bg-gradient-to-r from-orange-500 to-purple-500 p-3 sm:p-4 rounded-full w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
            <BanknotesIcon className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 px-4">Ready for Split Payments?</h3>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-sm mx-auto px-4">
            Add your bank account to receive <span className="font-semibold text-green-600">96.5% directly</span>
          </p>
          
          {/* Compact Benefits Grid */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 max-w-xs sm:max-w-sm mx-auto mb-6 sm:mb-8 px-4">
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 bg-white p-2 sm:p-3 rounded-lg">
              <div className="bg-green-100 p-1 sm:p-2 rounded-full flex-shrink-0">
                <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
              </div>
              <span>Instant verify</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 bg-white p-2 sm:p-3 rounded-lg">
              <div className="bg-orange-100 p-1 sm:p-2 rounded-full flex-shrink-0">
                <ShieldCheckIcon className="h-3 w-3 sm:h-4 sm:w-4 text-orange-600" />
              </div>
              <span>Secure</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 bg-white p-2 sm:p-3 rounded-lg">
              <div className="bg-green-100 p-1 sm:p-2 rounded-full flex-shrink-0">
                <CurrencyRupeeIcon className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
              </div>
              <span>Direct deposit</span>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 bg-white p-2 sm:p-3 rounded-lg">
              <div className="bg-purple-100 p-1 sm:p-2 rounded-full flex-shrink-0">
                <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
              </div>
              <span>2-5 seconds</span>
            </div>
          </div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowAddForm(true)}
            className="p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold transition-all duration-200 shadow-lg text-sm sm:text-base mb-4"
          >
            <span className="flex items-center gap-2">
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              Set Up Bank Account
            </span>
          </motion.button>
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {bankAccounts.map((account) => (
            <motion.div
              key={account.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 md:p-6 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className={`p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl flex-shrink-0 ${
                  account.status === 'verified' 
                    ? 'bg-gradient-to-r from-green-100 to-green-200' 
                    : 'bg-gradient-to-r from-yellow-100 to-yellow-200'
                }`}>
                  {account.status === 'verified' ? (
                    <CheckCircleIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-green-600" />
                  ) : (
                    <ClockIcon className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-yellow-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 truncate">{account.accountHolderName}</h3>
                        {account.isPrimary && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full flex-shrink-0">
                            Primary
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 mb-2">IFSC: {account.ifscCode}</p>
                        
                        {/* Comprehensive Status Information */}
                        {account.cashfreeStatus && (
                          <div className="mb-3 sm:mb-4">
                            <div className={`inline-flex items-center gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusColor(account.cashfreeStatus)}`}>
                              <div className="w-2 h-2 rounded-full bg-current"></div>
                              <span>{account.cashfreeStatus.replace(/_/g, ' ')}</span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{getStatusMessage(account.cashfreeStatus)}</p>
                            
                            {/* Detailed Status Issues */}
                            {account.vendorData && getDetailedStatusInfo(account.vendorData)?.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {getDetailedStatusInfo(account.vendorData).map((issue, index) => (
                                  <div key={index} className={`p-3 rounded-lg border-l-4 ${
                                    issue.severity === 'error' ? 'bg-red-50 border-red-400' : 'bg-yellow-50 border-yellow-400'
                                  }`}>
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <h4 className={`text-sm font-medium ${
                                          issue.severity === 'error' ? 'text-red-800' : 'text-yellow-800'
                                        }`}>
                                          {issue.title}
                                        </h4>
                                        <p className={`text-xs mt-1 ${
                                          issue.severity === 'error' ? 'text-red-600' : 'text-yellow-600'
                                        }`}>
                                          {issue.message}
                                        </p>
                                      </div>
                                      <button className={`ml-3 px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                                        issue.severity === 'error' 
                                          ? 'bg-red-100 hover:bg-red-200 text-red-700' 
                                          : 'bg-yellow-100 hover:bg-yellow-200 text-yellow-700'
                                      }`}>
                                        {issue.action}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Account Actions */}
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                              <button
                                onClick={() => setShowAccountNumber(prev => ({
                                  ...prev,
                                  [account.id]: !prev[account.id]
                                }))}
                                className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                              >
                                {showAccountNumber[account.id] ? (
                                  <>
                                    <EyeSlashIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline">Hide</span>
                                  </>
                                ) : (
                                  <>
                                    <EyeIcon className="h-4 w-4" />
                                    <span className="hidden sm:inline">Show</span>
                                  </>
                                )}
                              </button>
                              
                              {/* Refresh Status Button */}
                              {account.vendorId && (
                                <button
                                  onClick={() => checkVendorStatus(account.vendorId, account)}
                                  disabled={statusCheckLoading}
                                  className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  {statusCheckLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                                  ) : (
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                  )}
                                  <span className="hidden sm:inline">Refresh</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Settlement Schedule */}
                        {account.settlementSchedule && (
                          <div className="mb-2 text-xs sm:text-sm text-gray-600 bg-blue-50 px-2 py-1 rounded">
                            <span className="font-medium">Settlement:</span> {account.settlementSchedule}
                          </div>
                        )}

                        {/* Vendor ID */}
                        {account.vendorId && (
                          <div className="mb-2 text-xs text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded">
                            Vendor ID: {account.vendorId}
                          </div>
                        )}

                        {/* Action Buttons for Failed Validation */}
                        {account.cashfreeStatus === 'BANK_VALIDATION_FAILED' && (
                          <div className="mb-3 flex flex-wrap gap-2">
                            <button className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium rounded-lg transition-colors">
                              Update Bank Details
                            </button>
                            <button className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-medium rounded-lg transition-colors">
                              Contact Support
                            </button>
                          </div>
                        )}

                        {/* KYC Document Status */}
                        {(account.cashfreeStatus === 'KYC_PENDING' || account.cashfreeStatus === 'IN_BENE_CREATION') && (
                          <div className="mb-3">
                            <div className="text-xs font-medium text-gray-700 mb-2">KYC Documents:</div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600">PAN Card</span>
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Action Required</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600">GSTIN</span>
                                <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">Action Required</span>
                              </div>
                            </div>
                            <button className="mt-2 px-3 py-1.5 bg-orange-100 hover:bg-orange-200 text-orange-700 text-xs font-medium rounded-lg transition-colors">
                              Upload KYC Documents
                            </button>
                          </div>
                        )}

                        {account.splitPaymentsEnabled && (
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>
                            <span className="text-xs sm:text-sm text-green-600 font-medium">Split Payments Active</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                        <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                          account.status === 'verified' || account.status === 'validating'
                            ? account.status === 'verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {account.status === 'verified' ? 'Verified' : account.status === 'validating' ? 'Validating' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Add Bank Account Modal */}
        <AnimatePresence>
          {showAddForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 pb-30 sm:pb-8"
              onClick={() => setShowAddForm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-4 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
                        <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-lg sm:text-xl font-bold text-white">Add Bank Account</h2>
                        <p className="text-xs sm:text-sm text-blue-100">Verify for split payments</p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddForm(false)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                {/* Modal Content */}
                <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto">
                  {bankAccounts.length > 0 ? (
                    /* Show message when bank account already exists */
                    <div className="text-center py-8">
                      <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Bank Account Already Exists</h3>
                      <p className="text-gray-600 mb-6">You already have a bank account registered for this restaurant. Only one bank account is allowed per restaurant.</p>
                      <button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  ) : (
                    /* Show form when no bank account exists */
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                      {/* Bank Details Section */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bank Account Details</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Account Number *
                            </label>
                            <input
                              type="text"
                              name="accountNumber"
                              value={formData.accountNumber}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                              placeholder="Enter account number"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Confirm Account Number *
                            </label>
                            <input
                              type="text"
                              name="confirmAccountNumber"
                              value={formData.confirmAccountNumber}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                              placeholder="Re-enter account number"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              IFSC Code *
                            </label>
                            <input
                              type="text"
                              name="ifscCode"
                              value={formData.ifscCode}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                              placeholder="Enter IFSC code"
                              maxLength={11}
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Account Holder Name *
                            </label>
                            <input
                              type="text"
                              name="accountHolderName"
                              value={formData.accountHolderName}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base"
                              placeholder="Enter account holder name"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      {/* KYC Details Section */}
                      <div className="space-y-4 pt-4 border-t border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">KYC Details</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              PAN Card Number
                            </label>
                            <input
                              type="text"
                              name="panCard"
                              value={formData.panCard}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base uppercase"
                              placeholder="ABCDE1234F"
                              maxLength={10}
                            />
                            <p className="text-xs text-gray-500 mt-1">Format: ABCDE1234F</p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              GST Number
                            </label>
                            <input
                              type="text"
                              name="gstNumber"
                              value={formData.gstNumber}
                              onChange={handleInputChange}
                              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm sm:text-base uppercase"
                              placeholder="22AAAAA0000A1Z5"
                              maxLength={15}
                            />
                            <p className="text-xs text-gray-500 mt-1">15-digit GST number</p>
                          </div>
                        </div>

                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="text-sm text-orange-700">
                            <strong>Note:</strong> Either PAN card or GST number is required for KYC verification. 
                            Providing both will speed up the verification process.
                          </p>
                        </div>
                      </div>

                      {/* Validation Messages */}
                      {formData.accountNumber && formData.confirmAccountNumber && 
                       formData.accountNumber !== formData.confirmAccountNumber && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-600">Account numbers do not match</p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setShowAddForm(false)}
                          className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
                        >
                          Cancel
                        </button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          type="submit"
                          disabled={isLoading || (formData.accountNumber !== formData.confirmAccountNumber)}
                          className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                        >
                          {isLoading ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Verifying...
                            </span>
                          ) : (
                            'Verify & Add Account'
                          )}
                        </motion.button>
                      </div>
                    </form>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default BankAccountManagement
